/**
 * Privacy.jsx — EVORA Privacy Policy Page
 * 
 * Industry-grade privacy policy for Indian e-commerce.
 * Compliant with Indian data protection requirements and international standards.
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import { IconShield, IconArrowRight } from '../../components/common/Icons/Icons';
import './Privacy.css';

const SITE_URL    = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME  = BRAND?.name || 'EVORA';
const PRIVACY_URL = `${SITE_URL}/privacy`;
const OG_IMAGE    = `${SITE_URL}/og-image.jpg`;
const CONTACT_EMAIL = BRAND?.email || 'privacy@evoraclothing.com';
const LAST_UPDATED = 'January 1, 2026'; // Update this when policy changes

// ── JSON-LD Schema (module-level for performance) ─────────────────────────────
const privacySchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${PRIVACY_URL}#webpage`,
      url: PRIVACY_URL,
      name: `Privacy Policy | ${BRAND_NAME}`,
      description: `Privacy Policy for ${BRAND_NAME}. Learn how we collect, use, and protect your personal information.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${PRIVACY_URL}#breadcrumb` },
      dateModified: '2026-01-01',
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${PRIVACY_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: PRIVACY_URL },
      ],
    },
  ],
});

function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`Privacy Policy for ${BRAND_NAME}. Learn how we collect, use, and protect your personal information in compliance with Indian data protection laws.`}
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={PRIVACY_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={PRIVACY_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Privacy Policy | ${BRAND_NAME}`} />
        <meta property="og:description" content={`Privacy Policy for ${BRAND_NAME}. Learn how we protect your personal information.`} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Privacy Policy — ${BRAND_NAME}`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Privacy Policy | ${BRAND_NAME}`} />
        <meta name="twitter:description" content={`Privacy Policy for ${BRAND_NAME}. Learn how we protect your personal information.`} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`Privacy Policy — ${BRAND_NAME}`} />

        <script type="application/ld+json">{privacySchema}</script>
      </Helmet>

      <Layout showFooter={true} showBottomTab={true}>
        <main className="privacy-page" id="main-content">
          
          {/* ── Breadcrumb ─────────────────────────────────────────────── */}
          <nav className="privacy-breadcrumb" aria-label="Breadcrumb">
            <ol className="privacy-breadcrumb__list">
              <li>
                <Link to="/" className="privacy-breadcrumb__link">Home</Link>
              </li>
              <li className="privacy-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="privacy-breadcrumb__current">
                  Privacy Policy
                </span>
              </li>
            </ol>
          </nav>

          {/* ── Hero Section ─────────────────────────────────────────────── */}
          <header className="privacy-hero">
            <div className="privacy-hero__icon" aria-hidden="true">
              <IconShield size={48} />
            </div>
            <h1 className="privacy-hero__title">Privacy Policy</h1>
            <p className="privacy-hero__subtitle">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <p className="privacy-hero__updated">
              Last updated: <time dateTime="2026-01-01">{LAST_UPDATED}</time>
            </p>
          </header>

          {/* ── Main Content ─────────────────────────────────────────────── */}
          <article className="privacy-content">
            
            {/* ── Introduction ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="introduction-heading">
              <h2 id="introduction-heading" className="privacy-section__title">1. Introduction</h2>
              <div className="privacy-section__content">
                <p>
                  Welcome to {BRAND_NAME} ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
                  the security of your personal information. This Privacy Policy explains how we collect, use, disclose, 
                  and safeguard your information when you visit our website, make a purchase, or interact with our services.
                </p>
                <p>
                  By using our website or services, you agree to the collection and use of information in accordance with 
                  this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>
            </section>

            {/* ── Information We Collect ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="collect-heading">
              <h2 id="collect-heading" className="privacy-section__title">2. Information We Collect</h2>
              <div className="privacy-section__content">
                <h3 className="privacy-section__subtitle">2.1 Personal Information</h3>
                <p>We collect information that you provide directly to us, including:</p>
                <ul className="privacy-list">
                  <li><strong>Account Information:</strong> Name, email address, phone number, password (encrypted)</li>
                  <li><strong>Order Information:</strong> Billing and shipping addresses, payment information, order history</li>
                  <li><strong>Communication:</strong> Messages sent through our contact forms, customer support interactions</li>
                  <li><strong>Preferences:</strong> Size preferences, wishlist items, newsletter subscriptions</li>
                </ul>

                <h3 className="privacy-section__subtitle">2.2 Automatically Collected Information</h3>
                <p>When you visit our website, we automatically collect certain information:</p>
                <ul className="privacy-list">
                  <li><strong>Device Information:</strong> IP address, browser type, device type, operating system</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, referring URLs</li>
                  <li><strong>Cookies and Tracking:</strong> See our <Link to="/cookies" className="privacy-link">Cookies Policy</Link> for details</li>
                </ul>
              </div>
            </section>

            {/* ── How We Use Your Information ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="use-heading">
              <h2 id="use-heading" className="privacy-section__title">3. How We Use Your Information</h2>
              <div className="privacy-section__content">
                <p>We use the information we collect for the following purposes:</p>
                <ul className="privacy-list">
                  <li><strong>Order Processing:</strong> To process and fulfill your orders, send order confirmations, and provide customer support</li>
                  <li><strong>Account Management:</strong> To create and manage your account, process returns, and maintain your order history</li>
                  <li><strong>Communication:</strong> To respond to your inquiries, send important updates about your orders, and provide customer service</li>
                  <li><strong>Marketing:</strong> To send you promotional emails, newsletters, and updates about new products (only with your consent)</li>
                  <li><strong>Website Improvement:</strong> To analyze website usage, improve our services, and enhance user experience</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations, prevent fraud, and protect our rights and the rights of our users</li>
                </ul>
              </div>
            </section>

            {/* ── Information Sharing ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="sharing-heading">
              <h2 id="sharing-heading" className="privacy-section__title">4. Information Sharing and Disclosure</h2>
              <div className="privacy-section__content">
                <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
                
                <h3 className="privacy-section__subtitle">4.1 Service Providers</h3>
                <p>
                  We share information with trusted third-party service providers who assist us in operating our website, 
                  processing payments, shipping orders, and providing customer support. These providers are contractually 
                  obligated to protect your information and use it only for the purposes we specify.
                </p>

                <h3 className="privacy-section__subtitle">4.2 Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, court order, or government regulation, or to protect 
                  our rights, property, or safety, or that of our users or others.
                </p>

                <h3 className="privacy-section__subtitle">4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
                  acquiring entity, subject to the same privacy protections.
                </p>
              </div>
            </section>

            {/* ── Data Security ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="security-heading">
              <h2 id="security-heading" className="privacy-section__title">5. Data Security</h2>
              <div className="privacy-section__content">
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="privacy-list">
                  <li>SSL/TLS encryption for data transmission</li>
                  <li>Secure payment processing through PCI-DSS compliant gateways</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication procedures</li>
                  <li>Secure storage of sensitive information</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive 
                  to protect your information, we cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* ── Your Rights ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="rights-heading">
              <h2 id="rights-heading" className="privacy-section__title">6. Your Rights and Choices</h2>
              <div className="privacy-section__content">
                <p>You have the following rights regarding your personal information:</p>
                <ul className="privacy-list">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                  <li><strong>Objection:</strong> Object to processing of your information for marketing purposes</li>
                  <li><strong>Data Portability:</strong> Request transfer of your data to another service provider</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent is the legal basis</li>
                </ul>
                <p>
                  To exercise these rights, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="privacy-link">{CONTACT_EMAIL}</a> 
                  {' '}or through our <Link to="/contact" className="privacy-link">contact form</Link>.
                </p>
              </div>
            </section>

            {/* ── Cookies ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="cookies-heading">
              <h2 id="cookies-heading" className="privacy-section__title">7. Cookies and Tracking Technologies</h2>
              <div className="privacy-section__content">
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, 
                  and personalize content. For detailed information about our use of cookies, please see our{' '}
                  <Link to="/cookies" className="privacy-link">Cookies Policy</Link>.
                </p>
                <p>
                  You can control cookies through your browser settings. However, disabling cookies may limit your ability 
                  to use certain features of our website.
                </p>
              </div>
            </section>

            {/* ── Data Retention ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="retention-heading">
              <h2 id="retention-heading" className="privacy-section__title">8. Data Retention</h2>
              <div className="privacy-section__content">
                <p>
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
                  policy, unless a longer retention period is required or permitted by law. Order information is typically 
                  retained for 7 years for accounting and tax purposes.
                </p>
                <p>
                  When you request account deletion, we will delete or anonymize your personal information within 30 days, 
                  except where we are required to retain it for legal purposes.
                </p>
              </div>
            </section>

            {/* ── Children's Privacy ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="children-heading">
              <h2 id="children-heading" className="privacy-section__title">9. Children's Privacy</h2>
              <div className="privacy-section__content">
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a child, please contact us 
                  immediately, and we will take steps to delete such information.
                </p>
              </div>
            </section>

            {/* ── International Transfers ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="transfers-heading">
              <h2 id="transfers-heading" className="privacy-section__title">10. International Data Transfers</h2>
              <div className="privacy-section__content">
                <p>
                  Your information may be transferred to and processed in countries other than India, where data protection 
                  laws may differ. We ensure that appropriate safeguards are in place to protect your information in accordance 
                  with this Privacy Policy and applicable laws.
                </p>
              </div>
            </section>

            {/* ── Changes to This Policy ─────────────────────────────────────── */}
            <section className="privacy-section" aria-labelledby="changes-heading">
              <h2 id="changes-heading" className="privacy-section__title">11. Changes to This Privacy Policy</h2>
              <div className="privacy-section__content">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, 
                  operational, or regulatory reasons. We will notify you of any material changes by posting the new Privacy 
                  Policy on this page and updating the "Last updated" date.
                </p>
                <p>
                  We encourage you to review this Privacy Policy periodically to stay informed about how we protect your 
                  information.
                </p>
              </div>
            </section>

            {/* ── Contact Us ─────────────────────────────────────── */}
            <section className="privacy-section privacy-section--highlight" aria-labelledby="contact-heading">
              <h2 id="contact-heading" className="privacy-section__title">12. Contact Us</h2>
              <div className="privacy-section__content">
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <div className="privacy-contact">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="privacy-link">{CONTACT_EMAIL}</a>
                  </p>
                  <p>
                    <strong>Contact Form:</strong>{' '}
                    <Link to="/contact" className="privacy-link">Visit our Contact page</Link>
                  </p>
                  <p>
                    <strong>Address:</strong> {BRAND_NAME}, India
                  </p>
                </div>
              </div>
            </section>

          </article>

          {/* ── Related (internal linking) ─────────────────────────────── */}
          <nav className="privacy-related" aria-label="Related information">
            <h2 className="privacy-related__title">Related information</h2>
            <ul className="privacy-related__list" role="list">
              <li><Link to="/terms" className="privacy-related__link">Terms of Service</Link></li>
              <li><Link to="/cookies" className="privacy-related__link">Cookies Policy</Link></li>
              <li><Link to="/contact" className="privacy-related__link">Contact Us</Link></li>
              <li><Link to="/faq" className="privacy-related__link">FAQ</Link></li>
            </ul>
          </nav>

          {/* ── CTA Section ─────────────────────────────────────────────── */}
          <section className="privacy-cta">
            <h2 className="privacy-cta__title">Questions About Your Privacy?</h2>
            <p className="privacy-cta__text">
              We're here to help. Contact us if you have any questions or concerns about how we handle your personal information.
            </p>
            <div className="privacy-cta__actions">
              <Link to="/contact" className="privacy-cta__btn privacy-cta__btn--primary">
                Contact Us
                <IconArrowRight size={14} aria-hidden="true" />
              </Link>
              <Link to="/terms" className="privacy-cta__btn privacy-cta__btn--ghost">
                View Terms of Service
                <IconArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
          </section>

        </main>
      </Layout>
    </>
  );
}

export default Privacy;
